import moment, {isMoment} from 'moment-timezone';
import i18next from 'i18next';
import {formValueSelector} from 'redux-form';
import {css} from 'styled-components';

import createSelector from 'web-app/util/custom-create-selector';
import {type RootState} from 'web-app/react/redux/root-state';
import * as SessionSelectors from 'web-app/react/session/account/selectors';
import ChildPickupBehaviorIds from 'web-app/behaviors/child-pickup-behavior-ids';
import {GenericFieldType, type GenericFormData} from 'web-app/react/components/form/model';
import * as GoHomeWithChildrenEntitySelectors from 'web-app/react/entities/go-home-with-children/selectors';
import * as AccountSelectors from 'web-app/react/session/account/selectors';

import {type FormData} from './types';
import * as Constants from './constants';
import * as Actions from './actions';
import * as Helpers from './helpers';

const formSelector = formValueSelector(Constants.FormName);

const getCurrentPickupTime = (state: RootState) => formSelector(state, 'pickupTime');

export const childSignIn = (state: RootState) => state.modals.childPickup.childSignIn;

export const getEditingChildId = (state: RootState) => state.modals.childPickup.childId;

export const getIsUpdatingPickup = (state: RootState) => state.modals.childPickup.isUpdating;

export const getSelectedPickupId = (state: RootState) => state.modals.childPickup.childSignIn.pickupRelationId;

const getTimezoneFromCheckin = (state: RootState) => state.modals.childPickup.childSignIn.institution.timezone;

export const getInitialValues = createSelector(childSignIn, (extendedChildCheckin): FormData => {
    let pickupTimeMoment: moment.Moment | undefined;

    if (extendedChildCheckin.pickupTime) {
        const {pickupTime, institution} = extendedChildCheckin;
        pickupTimeMoment = moment.tz(pickupTime, institution.timezone);
    }

    return {
        pickupTime:
            pickupTimeMoment && isMoment(pickupTimeMoment)
                ? {hours: pickupTimeMoment.hours(), minutes: pickupTimeMoment.minutes()}
                : {hours: 15, minutes: 0}, // Default value if it hasn't been set should be 3pm
    };
});

const getSelectedTimeValues = createSelector(childSignIn, signIn => {
    const {fromHour, fromMinute, toHour, toMinute} = signIn.openingHours;

    return {
        fromHour,
        fromMinute,
        toHour,
        toMinute,
    };
});

export const getGoHomeWithChildId = (state: RootState) => state.modals.childPickup.goHomeWithChildId;

export const getFormData = createSelector(
    getSelectedTimeValues,
    getInitialValues,
    getGoHomeWithChildId,
    (state: RootState) => state.modals.childPickup.childSignIn.institution.showPickupUntilExactClosingTime,
    (selectedTimeValues, initialValues, goHomeWithChildId, showPickupUntilExactClosingTime): GenericFormData => {
        const {fromHour = 0, toHour = 0, toMinute = 0} = selectedTimeValues;

        const {latestPickupHour, latestPickupMinute} = Helpers.getLatestPickupTime({
            toHour,
            toMinute,
            showPickupUntilExactClosingTime,
        });

        return {
            fields: [
                {
                    name: 'pickupTime',
                    fieldType: GenericFieldType.newTimepicker,
                    label: i18next.t('checkin.pickupAt'),
                    value: initialValues.pickupTime,
                    elementCSS: css`
                        max-width: 150px;
                    `,
                    props: {
                        min: {
                            hours: fromHour,
                            minutes: 0,
                        },
                        max: {
                            hours: latestPickupHour,
                            minutes: latestPickupMinute,
                        },
                    },
                    onlyRenderIf: !goHomeWithChildId,
                },
            ],
        };
    },
);

export const getShouldShowOtherParentsPickupOption = createSelector(childSignIn, signIn => {
    return Boolean(signIn.behaviors.behaviorForId(ChildPickupBehaviorIds.ShowOtherParentsPickupOption));
});

export const initialValues = createSelector(
    (state: RootState) => state.modals.childPickup.childSignIn.pickupTime,
    getTimezoneFromCheckin,
    (pickupTime, timezone) => {
        if (!pickupTime || !timezone) {
            return undefined;
        }
        const mmt = moment.tz(pickupTime, timezone);
        const mmtMidnight = mmt.clone().startOf('day');
        const diffMinutes = mmt.diff(mmtMidnight, 'minutes');

        return {
            pickupTime: diffMinutes,
        };
    },
);

export const getIsPickupRequired = createSelector(
    AccountSelectors.getIsEmployee,
    (state: RootState) => state.modals.childPickup.childSignIn.requirePickupInfo,
    (isEmployee, isPickupInfoRequired) => !isEmployee && isPickupInfoRequired,
);

export const getCanCheckIn = createSelector(
    getIsPickupRequired,
    getSelectedPickupId,
    getCurrentPickupTime,
    (isPickupRequired, pickupId, pickupTime) => {
        if (isPickupRequired) {
            return Boolean(pickupId && pickupTime);
        }
        return true;
    },
);

const getOnSubmit = createSelector(
    getEditingChildId,
    getSelectedPickupId,
    getGoHomeWithChildId,
    getTimezoneFromCheckin,
    (childId, selectedPickupId, goHomeWithChildId, timezone) => {
        return (formValues: FormData) => {
            const pickupTime = formValues.pickupTime
                ? moment
                      .tz(timezone)
                      .startOf('day')
                      .minutes(formValues.pickupTime.hours * 60 + formValues.pickupTime.minutes)
                : null;

            Actions.updateCheckin.dispatch({
                childId,
                pickupTime,
                relationId: selectedPickupId,
                goHomeWithChildId,
            });
        };
    },
);

export const getFormConfig = createSelector(getFormData, getOnSubmit, (formData, onSubmit) => ({
    form: Constants.FormName,
    formData,
    onSubmit,
    reduxFormConfig: {
        destroyOnUnmount: false,
        enableReinitialize: true,
    },
    cancelForm: Actions.hide.dispatch,
    fieldsPerRow: 1,
}));

export const getFetchGoHomeWithForChildId = createSelector(
    SessionSelectors.getIsParent,
    getEditingChildId,
    (isParent, childId) => {
        return isParent ? childId : '';
    },
);

export const getGoingHomeWithChild = createSelector(GoHomeWithChildrenEntitySelectors.getChildById, child => {
    return child
        ? {
              id: child.id,
              image: child.profileImage.url,
              name: child.name.firstName,
          }
        : null;
});

export const getSelectedGoHomeWithChildId = createSelector(
    getGoHomeWithChildId,
    (state: RootState) => state.modals.childPickup.peoplePickerGoHomeWithChildId,
    (childId, peoplePickerChildId) => childId || peoplePickerChildId,
);

export const getIsGoHomeWithChildSelected = createSelector(getGoHomeWithChildId, childId => Boolean(childId));

export const canCreateRelation = createSelector(childSignIn, childSignIn => {
    return childSignIn.behaviors.includes(ChildPickupBehaviorIds.CanCreateRelation);
});
